import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import WishlistButton from './WishlistButton';
import LocalNotification from './LocalNotification';

const ProductCard = ({ product }) => {
  const { t } = useLanguage();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  const handleAddToCart = async (e) => {
    e.preventDefault();
    try {
      await addToCart(product, 1);
      setNotification({
        show: true,
        message: 'Article ajouté au panier avec succès !',
        type: 'success'
      });
    } catch (error) {
      setNotification({
        show: true,
        message: error.message || 'Erreur lors de l\'ajout au panier',
        type: 'error'
      });
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };


  return (
    <div className="group bg-gray-800 overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
      {/* Image Container */}
      <div className="relative overflow-hidden aspect-square">
        <Link to={`/products/${product.id}`} className="block w-full h-full">
          <img
            src={product.main_photo || 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=600&h=600&fit=crop&crop=center'}
            alt={product.name_fr}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 cursor-pointer"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=600&h=600&fit=crop&crop=center';
            }}
          />
        </Link>
        
        {/* Badges - Tous alignés à gauche, en haut, un par ligne */}
        <div className="absolute top-3 left-3 space-y-2 z-10">
          {/* Produit en vedette - Priorité haute */}
          {product.is_featured === 1 && (
            <div className="bg-yellow-500 text-gray-900 text-sm font-bold px-4 py-2 rounded-full shadow-lg min-w-[80px] text-center">
              ⭐ VEDETTE
            </div>
          )}
          
          {/* Nouveau produit */}
          {product.is_new === 1 && (
            <div className="bg-green-500 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg min-w-[80px] text-center">
              NOUVEAU
            </div>
          )}
          
          {/* Badge de réduction */}
          {(() => {
            const hasSale = product.sale_price && Number(product.sale_price) < Number(product.price);
            if (!hasSale) return null;
            const discount = Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100);
            return discount > 0 ? (
              <div className="bg-red-500 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg min-w-[80px] text-center">
                -{discount}%
              </div>
            ) : null;
          })()}
        </div>
        
        {/* Quick Actions */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <WishlistButton 
            product={product} 
            variant="filled"
            size={18}
            className="mb-2"
          />
        </div>
        
        {/* Add to Cart Button */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900/90 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="relative">
            <button
              onClick={handleAddToCart}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <ShoppingCart size={16} />
              <span>{t('product.addToCart')}</span>
            </button>
            
            {/* Local Notification */}
            <LocalNotification
              message={notification.message}
              type={notification.type}
              isVisible={notification.show}
              onClose={() => setNotification({ ...notification, show: false })}
              position="bottom"
            />
          </div>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4 flex flex-col min-h-52">
        {/* Category */}
        <p className="text-yellow-500 text-sm font-medium mb-2">
          {product.category_name || 'Collection Perse'}
        </p>
        
        {/* Product Name - Flexible height for better readability */}
        <Link to={`/products/${product.id}`} className="flex-1">
          <h3 className="text-white font-semibold text-lg mb-2 hover:text-yellow-500 transition-colors line-clamp-2 min-h-16 leading-tight">
            {product.name_fr}
          </h3>
        </Link>
        
        
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {product.material && (
            <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">
              {product.material}
            </span>
          )}
          {product.gemstone && (
            <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">
              {product.gemstone}
            </span>
          )}
        </div>
        
        {/* Price and Stock - Fixed at bottom */}
        <div className="mt-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {product.sale_price ? (
              <>
                <span className="text-yellow-500 font-bold text-lg">
                  {formatPrice(product.sale_price)}
                </span>
                <span className="text-gray-400 line-through text-sm">
                  {formatPrice(product.price)}
                </span>
              </>
            ) : (
              <span className="text-yellow-500 font-bold text-lg">
                {formatPrice(product.price)}
              </span>
            )}
          </div>
          
          {/* Stock */}
          <span className={`text-sm ${
            product.in_stock > 0 
              ? 'text-green-400' 
              : 'text-red-400'
          }`}>
            {product.in_stock > 0 
              ? `Stock: ${product.in_stock}` 
              : 'Rupture'
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductCard; 